import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addJointApplicationData,
} from "app/lib/store/slices/steps/loanTypesSteps";
import SubTitle from "app/components/common/typography/SubTitle";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { PersonalInfoFormStepTwo } from "app/utils/applicant/types/types";
import DynamicFormTwo from "app/components/Individual/applicant/personalDetails/DynamicFormTwo";

interface FormData {
  yourDetails: PersonalInfoFormStepTwo;
  coBorrowerDetails: PersonalInfoFormStepTwo;
}

const PersonalDetailsSetpTwo: React.FC = () => {
  const dispatch = useDispatch();
  const applicationData = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );
  const { loanApplication } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );
  const isSameAddress = loanApplication?.coApplicantsSameAddress;
  const coApplicantRelationship =
    loanApplication?.coApplicantRelationship === "spouse";

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    register,
    setValue,
    setError,
    clearErrors,
  } = useForm<FormData>({
    defaultValues: {
      yourDetails: {
        numberOfDependents: 0,
      },
      coBorrowerDetails: {
        numberOfDependents: 0,
      },
    },
  });

  const onSubmit = (data: FormData) => {
    const { coBorrowerDetails, yourDetails } = data;
    const { housingStatus, ...yourDetailsWithoutHousingStatus } =
      yourDetails || {};

    const {
      housingStatus: cohousingStatus,
      ...coBorrowerDetailsWithoutHousingStatus
    } = coBorrowerDetails || {};

    const yourDetailsData = {
      ...applicationData?.YourDetails,
      ...yourDetailsWithoutHousingStatus,
      ...(yourDetails?.numberOfDependents && {
        numberOfDependents: Number(yourDetails.numberOfDependents),
      }),
      ...(housingStatus && {
        mailingAddress: {
          housingStatus: housingStatus,
        },
      }),
    };

    const coBorrowerDetailsData = coApplicantRelationship
      ? {
          ...applicationData?.Coborrower,
          ...coBorrowerDetailsWithoutHousingStatus,
          numberOfDependents: Number(yourDetails.numberOfDependents),
          maritalStatus: yourDetails.maritalStatus,
          ...(housingStatus && {
            mailingAddress: {
              housingStatus: housingStatus,
            },
          }),
          address: {
            street: yourDetails.address.street,
            state: yourDetails.address.state,
            postCode: yourDetails.address.postCode,
          },
        }
      : {
          ...applicationData?.Coborrower,
          ...coBorrowerDetailsWithoutHousingStatus,
          ...(coBorrowerDetails?.numberOfDependents && {
            numberOfDependents: Number(coBorrowerDetails.numberOfDependents),
          }),
          ...(cohousingStatus && {
            mailingAddress: {
              housingStatus: cohousingStatus,
            },
          }),
          address: isSameAddress
            ? { ...yourDetails.address }
            : { ...coBorrowerDetails.address },
        };

    dispatch(
      addJointApplicationData({
        category: "YourDetails",
        data: yourDetailsData,
      })
    );
    dispatch(
      addJointApplicationData({
        category: "Coborrower",
        data: coBorrowerDetailsData,
      })
    );

    dispatch(nextStep());
  };

  useEffect(() => {
    if (applicationData.YourDetails && applicationData.Coborrower) {
      reset({
        yourDetails: {
          ...(applicationData.YourDetails?.numberOfDependents && {
            numberOfDependents: Number(
              applicationData.YourDetails.numberOfDependents
            ),
          }),
          maritalStatus: applicationData.YourDetails.maritalStatus,
          housingStatus:
            applicationData?.YourDetails?.mailingAddress?.housingStatus,
          address: {
            street: applicationData?.YourDetails?.address?.street || "",
            state: applicationData.YourDetails?.address?.state || "",
            postCode: applicationData.YourDetails?.address?.postCode || "",
          },
        },
        coBorrowerDetails: {
          ...(applicationData.Coborrower?.numberOfDependents && {
            numberOfDependents: Number(
              applicationData.Coborrower.numberOfDependents
            ),
          }),
          maritalStatus: applicationData.Coborrower.maritalStatus,
          housingStatus:
            applicationData?.Coborrower?.mailingAddress?.housingStatus,
          address: {
            street: applicationData.Coborrower?.address?.street || "",
            state: applicationData.Coborrower?.address?.state || "",
            postCode: applicationData.Coborrower?.address?.postCode || "",
          },
        },
      });
    }
  }, [applicationData, reset]);

  return (
    <>
      <ProgressBar progress={70} MB={4} />
      <div className="w-full  sm:w-[70%] xl:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Personal Details" />
        <SubTitle title="Please enter the required information below" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row gap-3 sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <DynamicFormTwo
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                numberDependentsName="yourDetails.numberOfDependents"
                maritalStatusName="yourDetails.maritalStatus"
                housingStatusName="yourDetails.housingStatus"
                addressStreetName="yourDetails.address.street"
                addressStateName="yourDetails.address.state"
                addressPostCodeName="yourDetails.address.postCode"
              />
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />
              <DynamicFormTwo
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                numberDependentsName="coBorrowerDetails.numberOfDependents"
                maritalStatusName="coBorrowerDetails.maritalStatus"
                housingStatusName="coBorrowerDetails.housingStatus"
                addressStreetName="coBorrowerDetails.address.street"
                addressStateName="coBorrowerDetails.address.state"
                addressPostCodeName="coBorrowerDetails.address.postCode"
                isSameAddress={isSameAddress}
                disabledCoFields={coApplicantRelationship}
              />
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit">Next</ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PersonalDetailsSetpTwo;
